import { css } from "lit";
const styles = css`/* stylelint-disable no-invalid-position-at-import-rule */
/* 
Omni-UI Scoped CSS: for projects primarily using Omni-UI through CSS, all Omni-UI styles
* are scoped under the .omni class to avoid conflicts with other styles
*/
.omni {
  --color-blue-50: #f2fcff;
  --color-blue-100: #caeaf4;
  --color-blue-200: #98d0e4;
  --color-blue-300: #65b1d0;
  --color-blue-400: #338fb6;
  --color-blue-500: #006a98;
  --color-blue-600: #005d85;
  --color-blue-700: #005072;
  --color-blue-800: #00425f;
  --color-blue-900: #00354c;
  --color-blue-1000: #002839;
  --color-grey-50: #feffff;
  --color-grey-100: #fafbfd;
  --color-grey-200: #f6f8fc;
  --color-grey-300: #f1f5fa;
  --color-grey-400: #cfd2d7;
  --color-grey-500: #adb0b3;
  --color-grey-600: #8b8d90;
  --color-grey-700: #686a6c;
  --color-grey-800: #464849;
  --color-grey-900: #242526;
  --color-grey-1000: #000;
  --color-green-50: #eefaf8;
  --color-green-100: #c7efe9;
  --color-green-200: #a1e5da;
  --color-green-300: #7adacb;
  --color-green-400: #53cfbc;
  --color-green-500: #2cc4ad;
  --color-green-600: #23a793;
  --color-green-700: #1a8978;
  --color-green-800: #136a5d;
  --color-green-900: #0c4a41;
  --color-green-1000: #062a24;
  --color-red-50: #fcf2f4;
  --color-red-100: #f7d2db;
  --color-red-200: #f1b3c3;
  --color-red-300: #eb94aa;
  --color-red-400: #e57591;
  --color-red-500: #df5678;
  --color-red-600: #bd4658;
  --color-red-700: #9b373c;
  --color-red-800: #782d29;
  --color-red-900: #54241c;
  --color-red-1000: #2f170f;
  --color-yellow-50: #fffcf5;
  --color-yellow-100: #fff5de;
  --color-yellow-200: #ffeec7;
  --color-yellow-300: #fee6b0;
  --color-yellow-400: #fedf99;
  --color-yellow-500: #fed882;
  --color-yellow-600: #d5b56d;
  --color-yellow-700: #ad9358;
  --color-yellow-800: #847044;
  --color-yellow-900: #5b4e2f;
  --color-yellow-1000: #332b1a;
  --color-white: var(--color-grey-50);
  --color-black: var(--color-grey-900);
  --text-primary: var(--color-blue-500);
  --text-primary-hover: var(--color-blue-600);
  --text-primary-pressed: var(--color-blue-700);
  --text-brand: var(--color-blue-500);
  --text-brand-hover: var(--color-blue-600);
  --text-brand-pressed: var(--color-blue-700);
  --text-neutral-default: var(--color-grey-900);
  --text-neutral-placeholder: var(--color-grey-700);
  --text-neutral-disabled: var(--color-grey-400);
  --text-neutral-disabled-dark: var(--color-grey-600);
  --text-neutral-info: var(--color-grey-800);
  --text-white: var(--color-grey-50);
  --text-error: var(--color-red-700);
  --text-success: var(--color-green-700);
  --icon-primary: var(--color-blue-500);
  --icon-primary-hover: var(--color-blue-600);
  --icon-primary-pressed: var(--color-blue-700);
  --icon-neutral-default: var(--color-grey-900);
  --icon-neutral-placeholder: var(--color-grey-700);
  --icon-neutral-disabled: var(--color-grey-400);
  --icon-neutral-info: var(--color-grey-800);
  --icon-neutral-disabled-dark: var(--color-grey-600);
  --icon-white: var(--color-grey-50);
  --icon-error: var(--color-red-700);
  --icon-success: var(--color-green-700);
  --fill-primary: var(--color-blue-500);
  --fill-primary-hover: var(--color-blue-600);
  --fill-primary-pressed: var(--color-blue-700);
  --fill-secondary: var(--color-grey-50);
  --fill-secondary-hover: var(--color-blue-50);
  --fill-secondary-pressed: var(--color-blue-100);
  --fill-default-light: var(--color-grey-300);
  --fill-navigation: var(--color-grey-300);
  --fill-default-dark: var(--color-grey-900);
  --fill-overlay: var(--color-grey-800);
  --fill-disabled-dark: var(--color-grey-600);
  --fill-disabled-light: var(--color-grey-400);
  --fill-info: var(--color-blue-200);
  --fill-error-default: var(--color-red-700);
  --fill-error-surface: var(--color-red-200);
  --fill-success-default: var(--color-green-700);
  --fill-success-surface: var(--color-green-100);
  --fill-alert: var(--color-yellow-500);
  --stroke-primary: var(--color-blue-500);
  --stroke-primary-hover: var(--color-blue-600);
  --stroke-primary-pressed: var(--color-blue-700);
  --stroke-default: var(--color-grey-900);
  --stroke-secondary: var(--color-grey-700);
  --stroke-tertiary: var(--color-grey-400);
  --stroke-disabled: var(--color-grey-400);
  --stroke-error: var(--color-red-700);
  --stroke-success: var(--color-green-700);
  --border-radius-none: 0;
  --border-radius-1: 0.2857142857142857rem;
  --border-radius-2: 0.5714285714285714rem;
  --border-radius-3: 0.8571428571428571rem;
  --border-radius-4: 1.1428571428571428rem;
  --border-radius-max: 7.142857142857143rem;
  --border-width-1: 0.07142857142857142rem;
  --shadow-1: 0px 2px 4px 0px rgba(0, 0, 0, 0.08), 0px 0px 2px 0px rgba(0, 0, 0, 0.12);
  --shadow-2: 0px 4px 8px 0px rgba(0, 0, 0, 0.12), 0px 0px 2px 0px rgba(0, 0, 0, 0.12);
  --shadow-3: 0px 8px 12px 0px rgba(0, 0, 0, 0.16), 0px 0px 2px 0px rgba(0, 0, 0, 0.12);
  --shadow-4: 0px 12px 16px 0px rgba(0, 0, 0, 0.2), 0px 0px 2px 0px rgba(0, 0, 0, 0.12);
  --shadow-5: 0px 24px 32px 0px rgba(0, 0, 0, 0.3), 0px 0px 2px 0px rgba(0, 0, 0, 0.12);
}
.omni button.tertiary,
.omni .button.tertiary, .omni button.secondary,
.omni .button.secondary {
  background-color: transparent;
  color: var(--text-primary);
  border: 1px solid var(--stroke-primary);
  padding: 7px 19px;
}
.omni button.tertiary omni-icon,
.omni .button.tertiary omni-icon, .omni button.secondary omni-icon,
.omni .button.secondary omni-icon {
  --color-icon-lines: var(--stroke-primary);
}
.omni button.tertiary:hover,
.omni .button.tertiary:hover, .omni button.secondary:hover,
.omni .button.secondary:hover, .omni button.hover.tertiary,
.omni .hover.button.tertiary, .omni button.hover.secondary,
.omni .hover.button.secondary, .omni button.tertiary:focus,
.omni .button.tertiary:focus, .omni button.secondary:focus,
.omni .button.secondary:focus, .omni button.focus.tertiary,
.omni .focus.button.tertiary, .omni button.focus.secondary,
.omni .focus.button.secondary {
  background-color: var(--fill-secondary-hover);
  border: 1px solid var(--stroke-primary-hover);
  color: var(--text-primary);
}
.omni button.tertiary:active,
.omni .button.tertiary:active, .omni button.secondary:active,
.omni .button.secondary:active, .omni button.active.tertiary,
.omni .active.button.tertiary, .omni button.active.secondary,
.omni .active.button.secondary {
  border: 1px solid var(--stroke-primary-pressed);
  background: var(--fill-secondary-pressed);
  color: var(--text-primary);
}
.omni button.tertiary:disabled,
.omni .button.tertiary:disabled, .omni button.secondary:disabled,
.omni .button.secondary:disabled, .omni button.disabled.tertiary,
.omni .disabled.button.tertiary, .omni button.disabled.secondary,
.omni .disabled.button.secondary {
  background-color: transparent;
  color: var(--text-neutral-disabled);
  border: 1px solid var(--stroke-disabled);
  cursor: not-allowed;
}
.omni button.tertiary:disabled omni-icon,
.omni .button.tertiary:disabled omni-icon, .omni button.secondary:disabled omni-icon,
.omni .button.secondary:disabled omni-icon, .omni button.disabled.tertiary omni-icon,
.omni .disabled.button.tertiary omni-icon, .omni button.disabled.secondary omni-icon,
.omni .disabled.button.secondary omni-icon {
  --color-icon-lines: var(--text-neutral-disabled);
}
.omni button.icon,
.omni .button.icon {
  padding: 8px 11px;
  border: 1px solid var(--stroke-primary);
  background-color: transparent;
  height: unset;
  width: unset;
}
.omni button.small.icon,
.omni .small.button.icon {
  padding: 6px 9px;
}
.omni button.large.icon,
.omni .large.button.icon {
  padding: 10px 14px;
}
.omni button.icon omni-icon,
.omni .button.icon omni-icon {
  --color-icon-lines: var(--stroke-primary);
}
.omni button.icon:hover,
.omni .button.icon:hover, .omni button.hover.icon,
.omni .hover.button.icon {
  background-color: var(--fill-primary-hover);
}
.omni button.icon:active,
.omni .button.icon:active, .omni button.active.icon,
.omni .active.button.icon {
  background-color: var(--fill-primary-pressed);
}
.omni button.icon:hover omni-icon,
.omni .button.icon:hover omni-icon, .omni button.icon:active omni-icon,
.omni .button.icon:active omni-icon, .omni button.hover.icon omni-icon,
.omni .hover.button.icon omni-icon, .omni button.active.icon omni-icon,
.omni .active.button.icon omni-icon, .omni button.icon:focus omni-icon,
.omni .button.icon:focus omni-icon, .omni button.focus.icon omni-icon,
.omni .focus.button.icon omni-icon {
  --color-icon-lines: var(--text-white);
}
.omni button.icon:disabled,
.omni .button.icon:disabled, .omni button.disabled.icon,
.omni .disabled.button.icon {
  cursor: not-allowed;
  background-color: transparent;
  border: 1px solid var(--stroke-disabled);
}
.omni button.icon:disabled omni-icon,
.omni .button.icon:disabled omni-icon, .omni button.disabled.icon omni-icon,
.omni .disabled.button.icon omni-icon {
  --color-icon-lines: var(--text-neutral-disabled);
}
.omni button,
.omni .button {
  box-sizing: border-box;
  /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
  font-family: "Open Sans";
  text-align: center;
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 14px;
  border: none;
  border-radius: var(--border-radius-2);
  transition: background-color 50ms ease-in-out;
  cursor: pointer;
  background-color: var(--fill-primary);
  color: white;
  display: inline-flex;
  height: 30px;
  padding: 8px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}
.omni button.small,
.omni .button.small {
  display: inline-flex;
  height: 24px;
  padding: 4px 14px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 12px;
}
.omni button.large,
.omni .button.large {
  display: inline-flex;
  height: 36px;
  padding: 10px 24px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  font-size: 16px;
  font-weight: 600;
  font-style: normal;
  line-height: 16px;
}
.omni button:hover, .omni button.hover, .omni button:focus, .omni button.focus,
.omni .button:hover,
.omni .button.hover,
.omni .button:focus,
.omni .button.focus {
  background-color: var(--fill-primary-hover);
  outline: none;
  color: white;
}
.omni button:active, .omni button.active,
.omni .button:active,
.omni .button.active {
  background-color: var(--fill-primary-pressed);
  outline: none;
  color: white;
}
.omni button:disabled, .omni button.disabled,
.omni .button:disabled,
.omni .button.disabled {
  background-color: var(--fill-disabled-light);
  color: var(--text-white);
  cursor: not-allowed;
}
.omni button omni-icon,
.omni .button omni-icon {
  --color-icon-lines: var(--text-white);
  width: 12.01px;
  height: 12.01px;
}
.omni button.tertiary,
.omni .button.tertiary {
  padding: 8.00002px 19.99998px;
  border: none;
}
.omni button.tertiary:hover, .omni button.tertiary.hover, .omni button.tertiary:active, .omni button.tertiary.active, .omni button.tertiary:disabled, .omni button.tertiary.disabled, .omni button.tertiary:focus, .omni button.tertiary.focus,
.omni .button.tertiary:hover,
.omni .button.tertiary.hover,
.omni .button.tertiary:active,
.omni .button.tertiary.active,
.omni .button.tertiary:disabled,
.omni .button.tertiary.disabled,
.omni .button.tertiary:focus,
.omni .button.tertiary.focus {
  border: none;
}
.omni button.tertiary:active, .omni button.tertiary.active,
.omni .button.tertiary:active,
.omni .button.tertiary.active {
  border-radius: var(--border-radius-2);
  background: var(--fill-secondary-pressed);
}
.omni button.tertiary:disabled, .omni button.tertiary.disabled,
.omni .button.tertiary:disabled,
.omni .button.tertiary.disabled {
  background-color: transparent;
  color: var(--text-neutral-disabled);
  cursor: not-allowed;
}
.omni button.tertiary:disabled omni-icon, .omni button.tertiary.disabled omni-icon,
.omni .button.tertiary:disabled omni-icon,
.omni .button.tertiary.disabled omni-icon {
  --color-icon-lines: var(--text-neutral-disabled);
}
.omni input,
.omni .input {
  /* stylelint-disable-next-line font-family-no-missing-generic-family-keyword */
  -moz-appearance: none;
  -webkit-appearance: none;
  appearance: none;
  font-family: "Open Sans";
  color: var(--text-neutral-info);
  font-size: 14px;
  display: inline-flex;
  box-shadow: none;
  align-items: center;
  justify-content: flex-start;
  padding: 0px 16px;
  height: 36px;
  border-radius: var(--border-radius-2, 8px);
  background: var(--fill-secondary);
  border: 1px solid var(--stroke-tertiary);
  min-width: 100px;
}
.omni input:active, .omni input.active, .omni input:focus,
.omni .input:active,
.omni .input.active,
.omni .input:focus {
  outline: none;
  border: 1px solid var(--stroke-primary);
}
.omni input.error,
.omni .input.error {
  border: 1px solid var(--stroke-error);
}
.omni input[disabled], .omni input.disabled,
.omni .input[disabled],
.omni .input.disabled {
  cursor: not-allowed;
  border: 1px solid var(--stroke-disabled);
  background: var(--fill-disabled-light);
}
.omni input[type=number],
.omni .input[type=number] {
  min-width: 50px;
}
.omni input[type=checkbox],
.omni input[type=color],
.omni input[type=radio],
.omni input[type=range] {
  appearance: revert;
  height: auto;
  padding: revert;
  min-width: revert;
}`;
var all_scoped_default = styles;
export {
  all_scoped_default as default
};
//# sourceMappingURL=all-scoped.js.map
